/**
 * Единый разбор FastAPI `detail` (строка, объект с message, массив Pydantic).
 * Используется в api-client-v2 и при ручном fetch.
 */

export type ApiValidationErrorItem = {
  field_key: string;
  message: string;
  geometry_id?: number | null;
  code?: string;
  applies_to?: string | null;
};

/** Поля validation_errors из detail карточки (422), если бэкенд их прислал. */
export function validationErrorsFromDetail(detail: unknown): ApiValidationErrorItem[] {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return [];
  const raw = (detail as { validation_errors?: unknown }).validation_errors;
  if (!Array.isArray(raw)) return [];
  const out: ApiValidationErrorItem[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as { field_key?: unknown; message?: unknown; geometry_id?: unknown; code?: unknown; applies_to?: unknown };
    if (typeof row.field_key !== "string" || typeof row.message !== "string" || !row.message.trim()) {
      continue;
    }
    out.push({
      field_key: row.field_key,
      message: row.message,
      geometry_id: typeof row.geometry_id === "number" ? row.geometry_id : null,
      code: typeof row.code === "string" ? row.code : undefined,
      applies_to: typeof row.applies_to === "string" ? row.applies_to : null,
    });
  }
  return out;
}

export class ApiClientError extends Error {
  readonly validation_errors: ApiValidationErrorItem[];

  constructor(message: string, detail: unknown) {
    super(message);
    this.name = "ApiClientError";
    this.validation_errors = validationErrorsFromDetail(detail);
  }
}

function validationMessages(detail: unknown): string[] {
  return validationErrorsFromDetail(detail).map((e) => e.message);
}

export function messageFromFastApiDetail(detail: unknown, fallback: string): string {
  if (detail === undefined || detail === null) return fallback;
  if (typeof detail === "string" && detail.length > 0) return detail;
  const fieldMsgs = validationMessages(detail);
  if (fieldMsgs.length > 0) {
    const shown = fieldMsgs.slice(0, 3).join("; ");
    return fieldMsgs.length > 3 ? `${shown}; …` : shown;
  }
  if (
    detail &&
    typeof detail === "object" &&
    !Array.isArray(detail) &&
    "message" in detail
  ) {
    const m = (detail as { message?: string }).message;
    if (typeof m === "string" && m.length > 0) return m;
  }
  if (Array.isArray(detail)) {
    const joined = detail
      .map((e: { loc?: unknown[]; msg?: string; type?: string }) => {
        const loc = Array.isArray(e.loc) ? e.loc.join(".") : "";
        const bit = loc ? `${loc}: ${e.msg ?? ""}` : String(e.msg ?? "");
        return e.type ? `${bit} (${e.type})` : bit;
      })
      .filter(Boolean)
      .join("; ");
    if (joined.length > 0) return joined;
  }
  return fallback;
}

/** Алиас из роадмапа §2 (геометрии и совместимые 422). */
export const detailMessage = messageFromFastApiDetail;

export function messageFromApiDetail(detail: unknown): string {
  return messageFromFastApiDetail(detail, "Ошибка валидации геометрии");
}

/** Сообщение из ошибки, брошенной api-client (обычно уже разобранный detail). */
export function messageFromThrownApiError(
  error: unknown,
  fallback = "Не удалось выполнить операцию",
): string {
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  return fallback;
}

/** После response.json() тела ошибки FastAPI */
export function messageFromErrorBody(body: { detail?: unknown } | null): string {
  if (!body) return "Неизвестная ошибка";
  return messageFromFastApiDetail(body.detail, "Неизвестная ошибка");
}

/** Для fetch без api-client: async readErrorMessage(response) */
export async function readFetchErrorMessage(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as { detail?: unknown };
    return messageFromErrorBody(data);
  } catch {
    return `HTTP ${response.status}`;
  }
}
